import React, { useEffect, useMemo, useState } from "react";
import { Navigate, useSearchParams } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { collection, doc, getDocs, onSnapshot } from "firebase/firestore";
import Sidebar from "../../DesignSystem/Sidebar";
import SummaryCards from "../../DesignSystem/SummaryCards/SummaryCards";
import FilterDrawer, {
  FilterDrawerApplyButton,
  FilterDrawerCheckboxOption,
  FilterDrawerEmpty,
  FilterDrawerResetButton,
  FilterDrawerSection,
} from "../../DesignSystem/FilterDrawer/FilterDrawer";
import CheckboxMultiSelect from "../../DesignSystem/Dropdowns/CheckboxMultiSelect";
import CustomSelect from "../../DesignSystem/Dropdowns/CustomSelect";
import SearchField from "../../DesignSystem/SearchField";
import Pagination from "../../DesignSystem/Pagination/Pagination";
import { useUserRole } from "../../../context/UserRoleContext";
import { useUserPermissions } from "../../../context/UserPermissionsContext";
import { useUserEmail } from "../../../context/UserEmailContext";
import { useBranding } from "../../../context/BrandingContext";
import { firestore } from "../../../backend/firebaseconfig";
import { canAccessMyPayments, isMyPaymentsStaff } from "./access";
import { requestEntries } from "./entriesService";
import { exportBusinessSummary } from "./exportBusinessSummary";
import {
  buildBusinessSummary,
  buildPlanCategoryLookup,
  ROSEMOUNT_CATEGORIES,
} from "./businessSummary";
import "../../../css/Payments/MyPayments.css";
import { ReactComponent as SortIcon } from "../../../assets/icons/Sort.svg";
import { ReactComponent as FilterIcon } from "../../../assets/icons/financialPromotions/Filter.svg";
import { ReactComponent as DownloadIcon } from "../../../assets/icons/download.svg";
import { ReactComponent as ArrowDownIcon } from "../../../assets/icons/arrow-down.svg";

const emptyFilters = () => ({
  recipients: [],
  policyAdvisers: [],
  commissionTypes: [],
  businessCategories: [],
});

const normaliseEmail = (value) => String(value || "").trim().toLowerCase();

const uniqueValues = (rows, field) =>
  [...new Set(rows.map((row) => String(row[field] || "").trim()).filter(Boolean))]
    .sort((left, right) => left.localeCompare(right, "en-GB", { sensitivity: "base" }));

const administratorAdviserReferences = (value) =>
  [...new Set((Array.isArray(value) ? value : value ? [value] : [])
    .flatMap((item) => {
      if (typeof item === "string") return item.split(/[\s,;]+/);
      if (!item || typeof item !== "object") return [];
      return [item.email, item.emailAddress, item.id, item.documentId];
    })
    .map(normaliseEmail)
    .filter(Boolean))]
    .sort((left, right) => left.localeCompare(right));

const administratorSnapshotRecord = (snapshot) => {
  const data = snapshot.data() || {};
  return {
    id: snapshot.id,
    email: normaliseEmail(data.email || data.emailAddress || snapshot.id),
    canViewPayments: data.canViewPayments === true,
    canViewGroupPayments: data.canViewGroupPayments === true,
    advisers: administratorAdviserReferences(data.advisers),
  };
};

const columns = [
  // ["Index", "Index"],
  ["Recipient", "Recipient"],
  ["PaymentCreated", "Payment Created"],
  ["PolicyAdviser", "Selling Adviser"],
  ["ClientName", "Client"],
  ["PolicyAccountNumber", "Policy Number"],
  ["PolicyProvider", "Provider"],
  ["BusinessType", "Business type"],
  ["CommissionType", "Commission type"],
  ["PaymentAmount", "Payment amount"],
  // ["GrossReceived", "Gross received"],
  ["NormalisedSplit", "Split"],
  // ["IntroducerName", "Introducers"],
];
const sortableFields = new Set([
  "Recipient",
  "PaymentCreated",
  "PolicyAdviser",
  "ClientName",
  "PolicyAccountNumber",
  "PolicyProvider",
  "BusinessType",
  "CommissionType",
  "PaymentAmount",
  "NormalisedSplit",
]);
const numericFields = new Set([
  "PaymentAmount",
  "GrossReceived",
  "NormalisedSplit",
]);
const rightAlignedFields = new Set([
  "PaymentAmount",
  "GrossReceived",
  "NormalisedSplit",
]);
const getCellClassName = (field, baseClassName = "") =>
  `${baseClassName} ${rightAlignedFields.has(field) ? "entries-table-cell-numeric" : ""}`.trim();
const getSortValue = (row, field) => {
  if (field === "NormalisedSplit") return Number(row.NormalisedSplit ?? row.Split);
  return Number(row[field]);
};
const searchableFields = [
  "Recipient",
  "PolicyAdviser",
  "ClientName",
  "PolicyAccountNumber",
  "PolicyProvider",
  "BusinessType",
  "CommissionType",
];
const money = new Intl.NumberFormat("en-GB", {
  style: "currency",
  currency: "GBP",
});
const PAGE_SIZE = 100;
const MINIMUM_INCOME_PERIOD_MONTH = "2026-09";
const incomePeriodMonthKey = (value) => {
  const match = String(value || "").match(/^(\d{4})-(\d{2})/);
  return match ? `${match[1]}-${match[2]}` : "";
};
const formatIncomePeriodMonth = (value) => {
  const monthKey = incomePeriodMonthKey(value);
  if (!monthKey) return "";
  const [year, month] = monthKey.split("-");
  return new Date(Date.UTC(Number(year), Number(month) - 1, 1)).toLocaleDateString(
    "en-GB",
    { month: "long", year: "numeric", timeZone: "UTC" },
  );
};
const incomePeriodOptionName = (period = {}) => {
  const startMonth = formatIncomePeriodMonth(
    period.startDatetime || period.incomePeriodName,
  );
  return startMonth || String(period.incomePeriodName || period.incomePeriodId || "");
};
const formatLastUpdated = (value) => {
  const date = new Date(value);
  return Number.isNaN(date.getTime())
    ? ""
    : date.toLocaleString("en-GB", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
        second: "2-digit",
        hour12: false,
      });
};
const getRosemountCategory = (row, categoryByPlanType) =>
  categoryByPlanType[
    String(row?.BusinessType || "").trim().toLocaleLowerCase("en-GB")
  ] || "Other";
const buildAdviserDisplayName = (data = {}, docId = "") => {
  const firstName = data.preferredName || data.firstName || data.forename || "";
  const preferredName = data.preferredName || "";
  const lastName = data.lastName || data.surname || "";
  const personName = [preferredName || firstName, lastName]
    .filter(Boolean)
    .join(" ")
    .trim();

  return (
    personName ||
    data.tradingName ||
    data.tradingStyle ||
    data.brandName ||
    data.email ||
    data.emailAddress ||
    docId
  );
};
function display(row, key, index) {
  if (key === "Index") return String(index + 1);
  const value = row[key];
  if (key === "NormalisedSplit") {
    const split = value ?? row.Split;
    return split === null || split === undefined || split === ""
      ? "-"
      : `${Number(split).toLocaleString("en-GB", { maximumFractionDigits: 4 })}%`;
  }
  if (key === "IntroducerName") return String(value ?? row.Introducers ?? "-");
  if (value === null || value === undefined || value === "") return "-";
  if (key === "PaymentAmount" || key === "GrossReceived")
    return money.format(Number(value));
  if (key === "PaymentCreated") {
    const date = new Date(value);
    return Number.isNaN(date.getTime())
      ? "-"
      : date.toLocaleDateString("en-GB", { timeZone: "UTC" });
  }
  return String(value);
}

function EntriesContent({ initialAdviser = "", isStaff, userEmail, userRole }) {
  const { tradingName, primaryColor } = useBranding();
  const isAdministrator =
    String(userRole || "").trim().toLowerCase() === "administrators";
  const [adviser, setAdviser] = useState(isStaff ? initialAdviser : "");
  const [selectedStaffAdvisers, setSelectedStaffAdvisers] = useState(
    isStaff && initialAdviser ? [initialAdviser] : [],
  );
  const [draftStaffAdvisers, setDraftStaffAdvisers] = useState(
    isStaff && initialAdviser ? [initialAdviser] : [],
  );
  const [options, setOptions] = useState([]);
  const [adviserNameByEmail, setAdviserNameByEmail] = useState({});
  const [adviserBrandingByEmail, setAdviserBrandingByEmail] = useState({});
  const [result, setResult] = useState(null);
  const [currentPeriodBoundary, setCurrentPeriodBoundary] = useState("");
  const [incomePeriods, setIncomePeriods] = useState([]);
  const [selectedIncomePeriod, setSelectedIncomePeriod] = useState("current");
  const [incomePeriodsLoading, setIncomePeriodsLoading] = useState(true);
  const [lastUpdated, setLastUpdated] = useState(() => new Date().toISOString());
  const [canViewPaymentsForScope, setCanViewPaymentsForScope] = useState(true);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [sort, setSort] = useState({ field: "", direction: "asc" });
  const [currentPage, setCurrentPage] = useState(1);
  const [expandedMobileRows, setExpandedMobileRows] = useState([]);
  const [planCategoryByType, setPlanCategoryByType] = useState({});
  const [filterOpen, setFilterOpen] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [filters, setFilters] = useState(emptyFilters);
  const [draftFilters, setDraftFilters] = useState(emptyFilters);
  const [administratorDirectoryRevision, setAdministratorDirectoryRevision] =
    useState(null);
  const [administratorScopeRevision, setAdministratorScopeRevision] =
    useState(null);
  const scopeEmails = useMemo(
    () => (isStaff ? [...new Set(selectedStaffAdvisers)] : []),
    [isStaff, selectedStaffAdvisers],
  );
  const scope = isStaff
    ? scopeEmails.join(",")
    : "self";
  const showAdviserSelector = isStaff && !initialAdviser;
  const key = `${scope}:${selectedIncomePeriod}`;
  const visible = result?.key === key ? result.data : null;
  const paymentPermissionEmail = normaliseEmail(isStaff ? adviser : userEmail);
  const selectedAdministratorEmails = useMemo(() => {
    if (isAdministrator) return [normaliseEmail(userEmail)].filter(Boolean);
    if (!isStaff) return [];
    return [...new Set(selectedStaffAdvisers
      .filter((value) => String(value).toLowerCase().startsWith("admin:"))
      .map((value) => normaliseEmail(String(value).slice("admin:".length)))
      .filter(Boolean))];
  }, [isAdministrator, isStaff, selectedStaffAdvisers, userEmail]);
  const selectedAdministratorKey = selectedAdministratorEmails.join(",");
  const incomePeriodOptions = useMemo(() => [
    {
      id: "current",
      name: currentPeriodBoundary
        ? formatIncomePeriodMonth(currentPeriodBoundary)
        : "Current commission month",
    },
    ...incomePeriods
      .filter((period) => incomePeriodMonthKey(
        period.startDatetime || period.incomePeriodName,
      ) >= MINIMUM_INCOME_PERIOD_MONTH)
      .map((period) => ({
        id: String(period.incomePeriodId),
        name: incomePeriodOptionName(period),
      })),
  ], [currentPeriodBoundary, incomePeriods]);
  const selectedIncomePeriodLabel =
    incomePeriodOptions.find((period) => period.id === selectedIncomePeriod)?.name ||
    "Current commission month";

  useEffect(() => {
    setDraftStaffAdvisers(selectedStaffAdvisers);
  }, [selectedStaffAdvisers]);

  useEffect(() => {
    if (!isStaff && !isAdministrator) {
      setAdministratorDirectoryRevision("");
      setAdministratorScopeRevision("");
      return undefined;
    }

    setAdministratorDirectoryRevision(null);
    setAdministratorScopeRevision(null);
    const selectedEmails = new Set(selectedAdministratorKey.split(",").filter(Boolean));
    return onSnapshot(
      collection(firestore, "Authorized Members", "Administrators", "Data"),
      (snapshot) => {
        const records = snapshot.docs
          .map(administratorSnapshotRecord)
          .sort((left, right) => left.id.localeCompare(right.id));
        setAdministratorDirectoryRevision(JSON.stringify(records));
        setAdministratorScopeRevision(JSON.stringify(
          records.filter((record) => selectedEmails.has(record.email)),
        ));
      },
      (error) => {
        console.error("[Business Summary] Unable to listen to Administrator payment scope:", error);
        setAdministratorDirectoryRevision("");
        setAdministratorScopeRevision("");
      },
    );
  }, [isAdministrator, isStaff, selectedAdministratorKey]);

  const getAdviserOptionLabel = (item = {}) => {
    const email = normaliseEmail(item.id || item.value || item.email);
    return adviserNameByEmail[email] || item.name || item.label || item.id;
  };
  const staffOptions = useMemo(
    () =>
      options.map((item) => ({
        label: getAdviserOptionLabel(item),
        value: item.id,
      })),
    [adviserNameByEmail, options],
  );
  const filterOptions = useMemo(() => {
    const rows = visible?.rows || [];
    return {
      recipients: uniqueValues(rows, "Recipient"),
      policyAdvisers: uniqueValues(rows, "PolicyAdviser"),
      commissionTypes: uniqueValues(rows, "CommissionType"),
      businessCategories: [
        ...new Set(rows.map((row) => getRosemountCategory(row, planCategoryByType))),
      ].sort((left, right) =>
        left.localeCompare(right, "en-GB", { sensitivity: "base" }),
      ),
    };
  }, [planCategoryByType, visible]);
  const activeFilterCount = Object.values(filters).reduce(
    (count, values) => count + values.length,
    0,
  );
  const toggleDraftFilter = (group, value) => {
    setDraftFilters((current) => ({
      ...current,
      [group]: current[group].includes(value)
        ? current[group].filter((item) => item !== value)
        : [...current[group], value],
    }));
  };
  const filteredRows = useMemo(() => {
    const source = visible?.rows || [];
    return source.filter(
      (row) =>
        (!filters.recipients.length || filters.recipients.includes(String(row.Recipient || "").trim())) &&
        (!filters.policyAdvisers.length || filters.policyAdvisers.includes(String(row.PolicyAdviser || "").trim())) &&
        (!filters.commissionTypes.length || filters.commissionTypes.includes(String(row.CommissionType || "").trim())) &&
        (!filters.businessCategories.length || filters.businessCategories.includes(getRosemountCategory(row, planCategoryByType))),
    );
  }, [filters, planCategoryByType, visible]);
  const shownRows = useMemo(() => {
    const term = search.trim().toLocaleLowerCase("en-GB");
    const filtered = term
      ? filteredRows.filter((row, index) => {
          const date = display(row, "PaymentCreated", index);
          const amount = display(row, "PaymentAmount", index);
          return [
            row.PaymentCreated,
            date,
            row.PaymentAmount,
            amount,
            ...searchableFields.map((field) => row[field]),
          ].some((value) =>
            String(value ?? "")
              .toLocaleLowerCase("en-GB")
              .includes(term),
          );
        })
      : filteredRows;
    if (!sort.field) return filtered;
    return filtered
      .map((row, position) => ({ row, position }))
      .sort((left, right) => {
        let comparison;
        if (sort.field === "PaymentCreated") {
          const leftTime = new Date(left.row.PaymentCreated).getTime();
          const rightTime = new Date(right.row.PaymentCreated).getTime();
          comparison =
            (Number.isNaN(leftTime) ? 0 : leftTime) -
            (Number.isNaN(rightTime) ? 0 : rightTime);
        } else if (numericFields.has(sort.field)) {
          const leftValue = getSortValue(left.row, sort.field);
          const rightValue = getSortValue(right.row, sort.field);
          comparison =
            (Number.isNaN(leftValue) ? 0 : leftValue) -
            (Number.isNaN(rightValue) ? 0 : rightValue);
        } else {
          comparison = String(left.row[sort.field] ?? "").localeCompare(
            String(right.row[sort.field] ?? ""),
            "en-GB",
            { sensitivity: "base" },
          );
        }
        if (comparison === 0) {
          return (
            Number(left.row.Index || left.position) -
            Number(right.row.Index || right.position)
          );
        }
        return sort.direction === "asc" ? comparison : -comparison;
      })
      .map(({ row }) => row);
  }, [filteredRows, search, sort]);
  const totalPages = Math.max(1, Math.ceil(shownRows.length / PAGE_SIZE));
  const pagedRows = useMemo(() => {
    const firstRow = (currentPage - 1) * PAGE_SIZE;
    return shownRows.slice(firstRow, firstRow + PAGE_SIZE);
  }, [currentPage, shownRows]);
  const businessSummary = useMemo(
    () => buildBusinessSummary(filteredRows, planCategoryByType),
    [filteredRows, planCategoryByType],
  );
  const summaryCards = useMemo(() => {
    const cards = [];
    const addCard = (amount, card) => {
      if (Number(amount) !== 0) {
        cards.push({ ...card, value: money.format(amount) });
      }
    };

    addCard(businessSummary.total, {
        id: "total-payment",
        label: "Total payment",
        tone: "cream",
    });
    addCard(businessSummary.newBusiness, {
        id: "new-business",
        label: "New business",
        tone: "blue",
    });
    addCard(businessSummary.ongoing, {
        id: "ongoing",
        label: "On-going",
        tone: "purple",
    });
    ROSEMOUNT_CATEGORIES.forEach((category) => {
      addCard(businessSummary.categoryTotals[category], {
        id: `category-${category}`,
        label: category,
        tone: "green",
      });
    });

    addCard(businessSummary.unmappedCategory, {
        id: "unmapped-category",
        label: "Other",
        tone: "green",
    });
    return cards;
  }, [businessSummary]);
  const headlineSummaryCards = summaryCards.filter((card) => card.tone !== "green");
  const categorySummaryCards = summaryCards.filter((card) => card.tone === "green");
  const exportBranding =
    (isStaff && adviserBrandingByEmail[normaliseEmail(adviser)]) || {};
  const handleExcelExport = async () => {
    setExporting(true);
    try {
      await exportBusinessSummary({
        rows: shownRows,
        summary: businessSummary,
        categoryNames: ROSEMOUNT_CATEGORIES,
        brandName: exportBranding.brandName || tradingName,
        primaryColor: exportBranding.primaryColor || primaryColor,
        incomePeriod: selectedIncomePeriodLabel,
        lastUpdated: formatLastUpdated(lastUpdated),
        search,
        filters,
      });
    } finally {
      setExporting(false);
    }
  };
  const changeSort = (field) => {
    setCurrentPage(1);
    setSort((current) => ({
      field,
      direction:
        current.field === field && current.direction === "asc" ? "desc" : "asc",
    }));
  };
  const toggleMobileRow = (rowKey) => {
    setExpandedMobileRows((current) =>
      current.includes(rowKey)
        ? current.filter((key) => key !== rowKey)
        : [...current, rowKey],
    );
  };

  useEffect(() => {
    if (currentPage > totalPages) setCurrentPage(totalPages);
  }, [currentPage, totalPages]);

  useEffect(() => {
    setCurrentPage(1);
  }, [key]);

  useEffect(() => {
    if (isStaff || isAdministrator) {
      setCanViewPaymentsForScope(true);
      return undefined;
    }

    if (!paymentPermissionEmail) {
      setCanViewPaymentsForScope(true);
      return undefined;
    }

    const adviserRef = doc(
      firestore,
      "Authorized Members",
      "Advisers",
      "Data",
      paymentPermissionEmail,
    );

    const unsubscribe = onSnapshot(
      adviserRef,
      (snapshot) => {
        const data = snapshot.exists() ? snapshot.data() || {} : {};
        const canViewPayments = data.canViewPayments === true;
        setCanViewPaymentsForScope(canViewPayments);

        if (!canViewPayments) {
          setResult(null);
        }
      },
      (error) => {
        console.error("[Business Summary] Unable to listen to adviser payment permissions:", error);
        setCanViewPaymentsForScope(false);
        setResult(null);
      },
    );

    return () => unsubscribe();
  }, [isAdministrator, isStaff, paymentPermissionEmail]);

  useEffect(() => {
    const controller = new AbortController();
    const suffix =
      isStaff && adviser ? `?adviser=${encodeURIComponent(adviser)}` : "";
    requestEntries(
      `/current-period${suffix}`,
      userEmail,
      userRole,
      controller.signal,
    )
      .then((data) => {
        if (!controller.signal.aborted) {
          setCurrentPeriodBoundary(data.currentPeriodBoundary || "");
          if (isStaff) {
            setCanViewPaymentsForScope(true);
          }
          setLastUpdated(
            data.lastUpdated ||
              data.updatedAt ||
              data.cache?.lastUpdated ||
              data.cache?.updatedAt ||
              data.cache?.refreshedAt ||
              data.cache?.generatedAt ||
              data.cache?.createdAt ||
              new Date().toISOString(),
          );
        }
      })
      .catch(() => {
        if (!controller.signal.aborted) {
          setCurrentPeriodBoundary("");
        }
      });
    return () => controller.abort();
  }, [adviser, administratorScopeRevision, isStaff, userEmail, userRole]);

  useEffect(() => {
    if (!isStaff) return undefined;
    let cancelled = false;

    const loadAdviserNames = async () => {
      const snapshot = await getDocs(
        collection(firestore, "Authorized Members", "Advisers", "Data"),
      );
      const entries = await Promise.all(
        snapshot.docs.map(async (docSnap) => {
          const root = docSnap.data() || {};
          let personal = {};

          try {
            const personalSnap = await getDocs(
              collection(
                firestore,
                "Authorized Members",
                "Advisers",
                "Data",
                docSnap.id,
                "Personal Details",
              ),
            );
            personal = personalSnap.docs.find((doc) => doc.id === "Data")?.data() || {};
          } catch {
            personal = {};
          }

          const email = normaliseEmail(
            root.email || personal.emailAddress || personal.email || docSnap.id,
          );
          return {
            email,
            name: buildAdviserDisplayName({ ...root, ...personal }, docSnap.id),
            brandName: root.brandName || root.tradingName || root.tradingStyle || "",
            primaryColor: root.primaryColor || "",
          };
        }),
      );

      if (!cancelled) {
        const validEntries = entries.filter((item) => item.email);
        setAdviserNameByEmail(
          Object.fromEntries(validEntries.map((item) => [item.email, item.name])),
        );
        setAdviserBrandingByEmail(
          Object.fromEntries(
            validEntries.map((item) => [
              item.email,
              { brandName: item.brandName, primaryColor: item.primaryColor },
            ]),
          ),
        );
      }
    };

    loadAdviserNames().catch(() => {
      if (!cancelled) {
        setAdviserNameByEmail({});
        setAdviserBrandingByEmail({});
      }
    });

    return () => {
      cancelled = true;
    };
  }, [isStaff]);

  useEffect(() => {
    if (!isStaff || administratorDirectoryRevision === null) return undefined;
    const controller = new AbortController();
    requestEntries("/advisers", userEmail, userRole, controller.signal)
      .then((data) => {
        if (!controller.signal.aborted) {
          const nextOptions = data.advisers || [];
          const allowed = new Set(nextOptions.map((item) => item.id));
          setOptions(nextOptions);
          setSelectedStaffAdvisers((current) =>
            current.filter((value) => allowed.has(value)),
          );
          setDraftStaffAdvisers((current) =>
            current.filter((value) => allowed.has(value)),
          );
          setAdviser((current) => current && !allowed.has(current) ? "" : current);
        }
      })
      .catch(() => {});
    return () => controller.abort();
  }, [administratorDirectoryRevision, isStaff, userEmail, userRole]);

  useEffect(() => {
    const controller = new AbortController();
    setIncomePeriodsLoading(true);
    requestEntries("/periods", userEmail, userRole, controller.signal)
      .then((data) => {
        if (!controller.signal.aborted) setIncomePeriods(data.periods || []);
      })
      .catch(() => {
        if (!controller.signal.aborted) setIncomePeriods([]);
      })
      .finally(() => {
        if (!controller.signal.aborted) setIncomePeriodsLoading(false);
      });
    return () => controller.abort();
  }, [userEmail, userRole]);

  useEffect(() => {
    setResult(null);
    if (!canViewPaymentsForScope) {
      setLoading(false);
      return undefined;
    }
    if (!scope) {
      setLoading(false);
      return undefined;
    }
    if ((isAdministrator || selectedAdministratorEmails.length > 0) &&
        administratorScopeRevision === null) {
      setLoading(false);
      return undefined;
    }
    const controller = new AbortController();
    setLoading(true);
    const params = new URLSearchParams({ all: "true" });
    params.set("incomePeriodId", selectedIncomePeriod);
    if (isStaff && adviser) params.set("adviser", adviser);
    if (scopeEmails.length > 1) {
      params.set("advisers", scopeEmails.join(","));
    }
    requestEntries(`?${params}`, userEmail, userRole, controller.signal)
      .then((data) => {
        if (!controller.signal.aborted) {
          setPlanCategoryByType(
            buildPlanCategoryLookup(data.planTypeMappings || []),
          );
          setResult({ key, data });
          const defaultFilters = {
            recipients: [],
            policyAdvisers: [],
            commissionTypes: [],
            businessCategories: [],
          };
          setFilters(defaultFilters);
          setDraftFilters(defaultFilters);
          if (selectedIncomePeriod === "current") {
            setCurrentPeriodBoundary(data.currentPeriodBoundary || "");
          }
          setLastUpdated(
            data.lastUpdated ||
              data.updatedAt ||
              data.cache?.lastUpdated ||
              data.cache?.updatedAt ||
              data.cache?.refreshedAt ||
              data.cache?.generatedAt ||
              data.cache?.createdAt ||
              new Date().toISOString(),
          );
        }
      })
      .catch(() => {})
      .finally(() => {
        if (!controller.signal.aborted) setLoading(false);
      });
    return () => controller.abort();
  }, [
    scope,
    adviser,
    administratorScopeRevision,
    canViewPaymentsForScope,
    isAdministrator,
    isStaff,
    key,
    scopeEmails,
    selectedAdministratorEmails.length,
    selectedIncomePeriod,
    userEmail,
    userRole,
  ]);

  return (
    <div className="entries-content gap-m py-s ">
      <div className="entries-heading">
        <div className="entries-heading-copy">
          <div className="FP_H3">Business Summary</div>
          {lastUpdated && (
            <div className="FP_Help_Text grey entries-last-updated">
              Last Updated: {formatLastUpdated(lastUpdated)}
            </div>
          )}
          <p className="FP_Help_Text_Semibold text-black">
            {selectedIncomePeriodLabel ? (
              <>
                Commission Month:{" "}
                <strong>{selectedIncomePeriodLabel}</strong>
              </>
            ) : loading ? (
              "Loading current period…"
            ) : (
              ""
            )}
          </p>
        </div>
        {(showAdviserSelector || scope || visible?.rows?.length > 0) && (
          <div className="entries-controls">
            <div className="entries-period-select">
              <CustomSelect
                label="Commission Month"
                options={incomePeriodOptions}
                value={selectedIncomePeriod}
                placeholder="Select commission month"
                disabled={incomePeriodsLoading}
                clearable={false}
                searchable
                arrowIcon={<ArrowDownIcon />}
                onChange={(event) => {
                  setSelectedIncomePeriod(event.target.value || "current");
                  setCurrentPage(1);
                }}
              />
            </div>
            {showAdviserSelector && (
              <div className="entries-select">
                <CheckboxMultiSelect
                  label="Adviser or Administrator"
                  options={staffOptions}
                  value={draftStaffAdvisers}
                  placeholder="Select advisers or administrators"
                  helperText="Select advisers or administrators whose entries you want to view"
                  onOpen={() => setDraftStaffAdvisers(selectedStaffAdvisers)}
                  onChange={setDraftStaffAdvisers}
                  onDone={() => {
                    const values = draftStaffAdvisers;
                    const nextAdviser =
                      values.find(
                        (value) => !selectedStaffAdvisers.includes(value),
                      ) ||
                      values[values.length - 1] ||
                      "";
                    setSelectedStaffAdvisers(values);
                    setAdviser(nextAdviser);
                  }}
                />
              </div>
            )}
            {visible?.rows?.length > 0 && (
              <div className="entries-table-tools">
                <div className="entries-search">
                  <SearchField
                    id="business-summary-search"
                    label="Search"
                    value={search}
                    placeholder="Search entries"
                    onChange={(event) => {
                      setSearch(event.target.value);
                      setCurrentPage(1);
                    }}
                  />
                </div>
                <button
                  type="button"
                  className="entries-filter-button"
                  aria-expanded={filterOpen}
                  aria-label={
                    activeFilterCount
                      ? `Filters, ${activeFilterCount} selected`
                      : "Filters"
                  }
                  title="Filters"
                  onClick={() => {
                    setDraftFilters({
                      recipients: [...filters.recipients],
                      policyAdvisers: [...filters.policyAdvisers],
                      commissionTypes: [...filters.commissionTypes],
                      businessCategories: [...filters.businessCategories],
                    });
                    setFilterOpen(true);
                  }}
                >
                  <FilterIcon aria-hidden="true" />
                </button>
                <button
                  type="button"
                  className="entries-filter-button entries-export-button"
                  disabled={exporting || !shownRows.length}
                  onClick={handleExcelExport}
                  aria-label={exporting ? "Exporting to Excel" : "Export to Excel"}
                  title={exporting ? "Exporting…" : "Export to Excel"}
                >
                  <DownloadIcon aria-hidden="true" />
                </button>
              </div>
            )}
          </div>
        )}
      </div>
      {/* {!scope && (
        <p className="FP_Content">
          Select an adviser to view their current-period entries.
        </p>
      )} */}
      {!canViewPaymentsForScope && (
        <p className="FP_Content" role="status">
          Payment access is not enabled for this adviser.
        </p>
      )}
      {scope && loading && (
        <p className="FP_Content" role="status">
          Loading entries…
        </p>
      )}
      {visible && (
        <div className="entries-results gap-m">
          {/* <p className="FP_Content">
            {shownRows.length} entries · Total payment amount:{" "}
            <strong className="FP_Content_Semibold">
              {money.format(shownPaymentAmount)}
            </strong>
          </p> */}
          {summaryCards.length > 0 && (
            <div className="business-summary-card-groups">
              {headlineSummaryCards.length > 0 && (
                <SummaryCards cards={headlineSummaryCards} grid="3" fitRow />
              )}
              {categorySummaryCards.length > 0 && (
                <SummaryCards
                  cards={categorySummaryCards}
                  className="business-summary-category-cards"
                  grid={String(Math.max(3, categorySummaryCards.length))}
                  fitRow
                />
              )}
            </div>
          )}
          {/* <section className="commission-classification card-corner-radius">
            <h2 className="FP_H4">Commission type classification</h2>
            <p className="FP_Help_Text text-primary-1">
              These rules determine the New Business and On-going totals above.
            </p>
            <div className="commission-classification-grid">
              {businessSummary.commissionTypes.map((item) => (
                <div className="commission-classification-item" key={item.type}>
                  <span className="FP_Help_Text_Semibold">{item.type}</span>
                  <span className="FP_Help_Text">{item.classification}</span>
                  <strong className="FP_Help_Text_Semibold">
                    {money.format(item.amount)}
                  </strong>
                </div>
              ))}
            </div>
          </section> */}

          {shownRows.length ? (
            <section className="card-corner-radius table-scroll-card entries-table-card">
              <div className="table-scroll-container entries-table-scroll entries-desktop-table">
                <table>
                  <thead>
                    <tr>
                      {columns.map(([field, label]) => (
                        <th
                          key={field}
                          scope="col"
                          className={getCellClassName(
                            field,
                            "FP_Help_Text_Semibold uppercase white-text",
                          )}
                          aria-sort={
                            sort.field === field
                              ? sort.direction === "asc"
                                ? "ascending"
                                : "descending"
                              : undefined
                          }
                        >
                          {sortableFields.has(field) ? (
                            <button
                              type="button"
                              className={getCellClassName(
                                field,
                                "table-sort-button table-heading-content",
                              )}
                              onClick={() => changeSort(field)}
                              aria-label={`Sort by ${label} ${sort.field === field && sort.direction === "asc" ? "descending" : "ascending"}`}
                            >
                              <span>{label}</span>
                              <span
                                aria-hidden="true"
                                className="entries-sort-indicator"
                              >
                                {sort.field === field ? (
                                  sort.direction === "asc" ? (
                                    "▲"
                                  ) : (
                                    "▼"
                                  )
                                ) : (
                                  <SortIcon />
                                )}
                              </span>
                            </button>
                          ) : (
                            label
                          )}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {pagedRows.map((row, index) => (
                      <tr key={`${row.PaymentId}-${index}`}>
                        {columns.map(([field]) => (
                          <td
                            key={field}
                            className={getCellClassName(
                              field,
                              "FP_Help_Text text-black",
                            )}
                          >
                            {field === "ClientName" && row.ClientUrl ? (
                              <a
                                className="table-url-link"
                                href={row.ClientUrl}
                                target="_blank"
                                rel="noreferrer"
                              >
                                {display(
                                  row,
                                  field,
                                  (currentPage - 1) * PAGE_SIZE + index,
                                )}
                              </a>
                            ) : field === "PolicyAccountNumber" && row.PlanUrl ? (
                              <a
                                className="table-url-link"
                                href={row.PlanUrl}
                                target="_blank"
                                rel="noreferrer"
                              >
                                {display(
                                  row,
                                  field,
                                  (currentPage - 1) * PAGE_SIZE + index,
                                )}
                              </a>
                            ) : (
                              display(
                                row,
                                field,
                                (currentPage - 1) * PAGE_SIZE + index,
                              )
                            )}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div className="entries-mobile-table-wrap">
                <table className="entries-mobile-table">
                  <thead>
                    <tr>
                      <th aria-label="Expand row" />
                      <th className="FP_Help_Text_Semibold">Client name</th>
                      <th className="FP_Help_Text_Semibold">Policy Number</th>
                      <th className="FP_Help_Text_Semibold">Business type</th>
                      <th className="entries-table-cell-numeric FP_Help_Text_Semibold">
                        Payment amount
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {pagedRows.map((row, index) => {
                      const rowKey = `${row.PaymentId || "row"}-${(currentPage - 1) * PAGE_SIZE + index}`;
                      const isExpanded = expandedMobileRows.includes(rowKey);
                      return (
                        <React.Fragment key={rowKey}>
                          <tr className="entries-mobile-summary-row">
                            <td>
                              <button
                                type="button"
                                className="entries-mobile-expand"
                                aria-expanded={isExpanded}
                                aria-label={`${isExpanded ? "Collapse" : "Expand"} ${display(row, "ClientName", index)}`}
                                onClick={() => toggleMobileRow(rowKey)}
                              >
                                <span aria-hidden="true" />
                              </button>
                            </td>
                            <td className="FP_Help_Text text-primary-1">
                              {row.ClientUrl ? (
                                <a
                                  className="table-url-link"
                                  href={row.ClientUrl}
                                  target="_blank"
                                  rel="noreferrer"
                                >
                                  {display(row, "ClientName", index)}
                                </a>
                              ) : (
                                display(row, "ClientName", index)
                              )}
                            </td>
                            <td className="FP_Help_Text">
                              {row.PlanUrl ? (
                                <a
                                  className="table-url-link"
                                  href={row.PlanUrl}
                                  target="_blank"
                                  rel="noreferrer"
                                >
                                  {display(row, "PolicyAccountNumber", index)}
                                </a>
                              ) : (
                                display(row, "PolicyAccountNumber", index)
                              )}
                            </td>
                            <td className="FP_Help_Text">
                              {display(row, "BusinessType", index)}
                            </td>
                            <td className="entries-table-cell-numeric FP_Help_Text">
                              {display(row, "PaymentAmount", index)}
                            </td>
                          </tr>
                          {isExpanded && (
                            <tr className="entries-mobile-detail-row">
                              <td />
                              <td colSpan="4">
                                <dl>
                                  {[
                                    ["Recipient", "Recipient"],
                                    ["Payment created", "PaymentCreated"],
                                    ["Selling Adviser", "PolicyAdviser"],
                                    ["Policy provider", "PolicyProvider"],
                                    ["Commission type", "CommissionType"],
                                    ["Adviser split", "NormalisedSplit"],
                                  ].map(([label, field]) => (
                                    <div key={field}>
                                      <dt className="FP_Help_Text">{label}</dt>
                                      <dd className="FP_Help_Text text-black">
                                        {display(row, field, index)}
                                      </dd>
                                    </div>
                                  ))}
                                </dl>
                              </td>
                            </tr>
                          )}
                        </React.Fragment>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </section>
          ) : visible.rows.length ? (
            <p className="FP_Content">No matching entries found.</p>
          ) : (
            <p className="FP_Content">No entries for the current period.</p>
          )}
          {shownRows.length > PAGE_SIZE && (
            <div className="entries-pagination">
              <Pagination
                currentPage={currentPage}
                totalPages={totalPages}
                onPageChange={setCurrentPage}
              />
            </div>
          )}
        </div>
      )}
      <FilterDrawer
        open={filterOpen}
        onClose={() => setFilterOpen(false)}
        footer={
          <>
            <FilterDrawerApplyButton
              onClick={() => {
                setFilters(draftFilters);
                setCurrentPage(1);
                setFilterOpen(false);
              }}
            >
              Apply Filters
            </FilterDrawerApplyButton>
            <FilterDrawerResetButton
              onClick={() => {
                const cleared = emptyFilters();
                setDraftFilters(cleared);
                setFilters(emptyFilters());
                setCurrentPage(1);
              }}
            >
              Reset All
            </FilterDrawerResetButton>
          </>
        }
      >
        {[
          ["recipients", "Recipient"],
          ["policyAdvisers", "Selling Adviser"],
          ["commissionTypes", "Commission Type"],
          ["businessCategories", "Business Category"],
        ].map(([group, title]) => (
          <FilterDrawerSection title={title} key={group}>
            {filterOptions[group].length ? (
              filterOptions[group].map((value) => (
                <FilterDrawerCheckboxOption
                  key={value}
                  label={value}
                  checked={draftFilters[group].includes(value)}
                  onChange={() => toggleDraftFilter(group, value)}
                />
              ))
            ) : (
              <FilterDrawerEmpty>No options available</FilterDrawerEmpty>
            )}
          </FilterDrawerSection>
        ))}
      </FilterDrawer>
    </div>
  );
}

export default function MyPayments() {
  const [searchParams] = useSearchParams();
  const { userRole } = useUserRole();
  const { userPermissions } = useUserPermissions();
  const { userEmail } = useUserEmail();
  const isStaff = isMyPaymentsStaff(userRole, userPermissions);
  const initialAdviser = isStaff ? searchParams.get("adviser") || "" : "";
  if (!canAccessMyPayments(userRole, userPermissions))
    return <Navigate to="/my-dashboard" replace />;
  return (
    <div className="Main-Page">
      <Helmet>
        <title>Business Summary</title>
      </Helmet>
      <Sidebar isSetExpanded={true} />
      <main className="Main-Container Lavender px-s my-payments-container">
        <section className="my-entries">
          <EntriesContent
            key={`${userRole}:${userEmail}:${initialAdviser}`}
            initialAdviser={initialAdviser}
            userEmail={userEmail}
            userRole={userRole}
            isStaff={isStaff}
          />
        </section>
      </main>
    </div>
  );
}
